import { z } from "zod";
import { getPlainTextFromDoc } from "../../tiptap/plaintext";
import { PollState, Post } from "../posts";
import { ReactionType } from "../reaction";
import { TipTapDocSchema } from "../tiptapDoc";

export const schema = {
    posts: {
        latest: {
            input: z.object({
                limit: z.number().min(1).max(100),
                cursor: z.number().nullish(),
            }),
            output: z.object({
                posts: z.array(Post),
                nextCursor: z.number().nullish(),
            }),
        },
        get: {
            input: z.object({
                postId: z.number(),
            }),
            output: Post,
        },
        create: {
            input: z
                .object({
                    contentJson: TipTapDocSchema,
                    attachmentIds: z.array(z.string()).optional(),
                    title: z.string().min(1).max(120),
                    isAnonymous: z.boolean(),
                    pollOptions: z.array(z.string()).optional(),
                })
                .refine(
                    (data) =>
                        getPlainTextFromDoc(data.contentJson).trim().length > 0 ||
                        (data.attachmentIds && data.attachmentIds.length > 0) ||
                        (data.pollOptions && data.pollOptions.length > 0),
                    { message: "Post must have content, attachments, or poll options" },
                )
                .refine(
                    (data) =>
                        !data.pollOptions || data.pollOptions.every((o) => o.trim().length > 0),
                    { message: "All poll options must be filled in" },
                )
                .refine(
                    (data) => {
                        if (!data.pollOptions || data.pollOptions.length === 0) return true;
                        const unique = new Set(data.pollOptions.map((o) => o.trim().toLowerCase()));
                        return unique.size === data.pollOptions.length;
                    },
                    { message: "Poll options must be unique" },
                ),
            output: z.void(),
        },
        delete: {
            input: z.object({
                postId: z.number(),
            }),
            output: z.void(),
        },
        update: {
            input: z
                .object({
                    contentJson: TipTapDocSchema,
                    attachmentIds: z.array(z.string()).optional(),
                    title: z.string().min(1).max(120),
                    postId: z.number(),
                    pollOptions: z.array(z.string()).optional(),
                })
                .refine(
                    (data) =>
                        !data.pollOptions || data.pollOptions.every((o) => o.trim().length > 0),
                    { message: "All poll options must be filled in" },
                )
                .refine(
                    (data) => {
                        if (!data.pollOptions || data.pollOptions.length === 0) return true;
                        const unique = new Set(data.pollOptions.map((o) => o.trim().toLowerCase()));
                        return unique.size === data.pollOptions.length;
                    },
                    { message: "Poll options must be unique" },
                ),
            output: Post,
        },
        react: {
            input: z.object({
                postId: z.number(),
                reaction: ReactionType,
            }),
            output: z.string(),
        },
        vote: {
            input: z.object({
                postId: z.number(),
                optionId: z.number(),
            }),
            // Return the updated poll state so clients can setQueryData in-memory
            // instead of round-tripping a refetch (PRTL-1139 / -1119 / -1120).
            output: PollState,
        },
    },
    postComments: {
        create: {
            input: z.object({
                contentJson: TipTapDocSchema,
                postId: z.number(),
                isAnonymous: z.boolean(),
            }),
            output: z.void(),
        },
        delete: {
            input: z.object({
                commentId: z.number(),
            }),
            output: z.void(),
        },
        update: {
            input: z.object({
                commentId: z.number(),
                contentJson: TipTapDocSchema,
            }),
            output: z.void(),
        },
        react: {
            input: z.object({
                commentId: z.number(),
                reaction: ReactionType,
            }),
            output: z.string(),
        },
    },
};
